// imageSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import AxiosInstance from "../../Service/AxiosInstance";
import { AnnotationLabelDetails } from "../../types/interface/Annotation.interface";

// Define types for the image response and error handling
export interface Image {
  id: string;
  imageUrl: string;
  thumbUrl: string;
  annotations: any[];
  projectId: string | null;
  annotationsCount: number;
  labelDetails: AnnotationLabelDetails[];
  isAnnotator: boolean;
  isAnnotated: boolean;
  taskTypeId?: string;
}

export interface ImageState {
  annotationImages: any[]; // Replace `any` with the correct type if possible
  annotationImageDetails: any[];
  allAnnotationImages: any[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  annotatedImageCount: number;
  pageSize: number;
  favouritesList: any[]; // Replace `any` with the correct type if possible
  projectImagesUploaded: Image[] | undefined;
  images: Image[];
  totalCountUploaded: number; // Add these properties
  totalPagesUploaded: number;
  currentPageUploaded: number;
  pageSizeUploaded: number;
  annotatedImageCountUploaded: number;
  imageLabelDetails: ImageDetails;
  isAdmin: boolean;
  isUploader: boolean;
}

export interface LableDetails {
  labelName: string;
  labelColor: string;
  count: number;
}

export interface ImageDetails {
  labels: LableDetails[];
  lastAnnnotatedDate: string;
  imageSize: number;
  imageHeight: number;
  imageWidth: number;
}

interface ExtendedImageState extends ImageState {
  listofAiModels: any[];
  listofAiLabels: any[];
}

const initialState: ExtendedImageState = {
  listofAiModels: [],
  listofAiLabels: [],

  annotationImages: [],
  annotationImageDetails: [],
  allAnnotationImages: [],
  annotatedImageCount: 0,
  loading: false,
  error: null,
  totalCount: 0,
  totalPages: 0,
  currentPage: 0,
  pageSize: 0,
  annotatedImageCountUploaded: 0,
  totalCountUploaded: 0,
  totalPagesUploaded: 0,
  currentPageUploaded: 0,
  pageSizeUploaded: 0,
  favouritesList: [],
  images: [],
  projectImagesUploaded: undefined,
  imageLabelDetails: {
    labels: [],
    lastAnnnotatedDate: "",
    imageSize: 0,
    imageHeight: 0,
    imageWidth: 0,
  },
  isAdmin: false,
  isUploader: false,
};

export interface FetchImagesParams {
  projectId: string;
  page: number;
  count: number;
  annotation?: string;
}

export interface ImageDetailsParams {
  projectImageId: string;
}

export interface FetchImagesLabelParams {
  labelId: string;
  projectId: string;
  page: number;
  count: number;
}

export interface FetchImagesParamsUploaded {
  projectId: string;
  currPage: number;
  count: number;
}

export interface UpdateImagePayload {
  id: string;
  updatedImage: Image;
}

export interface UpdateImageLabelColorPayload {
  labelId: string;
  color: string;
}

// Async thunk for fetching images
export const fetchImages = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    isAdmin: boolean;
    isUploader: boolean;
    imageUrlModel: Image[];
  },
  FetchImagesParams
>("images/fetchImages", async ({ projectId, page, count, annotation }) => {
  const response = await AxiosInstance.get(
    `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetAll?projectId=${projectId}&page=${page}&pageSize=${count}&annotation=${annotation}`
  );
  // Return the complete response data
  return response.data;
});

// Async thunk for fetching labels by project with pagination and annotation filter
export interface FetchLabelsByProjectPagedParams {
  projectId: string;
  page: number;
  pageSize: number;
  annotation?: string;
}

export const fetchLabelsByProjectPaged = createAsyncThunk<
  any, // Replace with a more specific type if available
  FetchLabelsByProjectPagedParams
>(
  "images/fetchLabelsByProjectPaged",
  async ({ projectId, page, pageSize, annotation }, { rejectWithValue }) => {
    try {
      let url = `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetLabelsByProjectPaged?projectId=${projectId}&page=${page}&pageSize=${pageSize}`;
      if (annotation !== undefined) {
        url += `&annotation=${annotation}`;
      }
      const response = await AxiosInstance.get(url);
      return response.data;
    } catch (error: any) {
      // Log the error for debugging
      console.error("Error in fetchLabelsByProjectPaged thunk:", error);
      // Return the error message or a default one
      return rejectWithValue(
        error.response?.data || "Error fetching labels by project paged"
      );
    }
  }
);

// Async thunk for fetching images
export const fetchImagesNew = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    isUploader: boolean;
    imageUrlModel: Image[];
  },
  FetchImagesParams
>("images/fetchImagesNew", async ({ projectId, page, count, annotation }) => {
  let url = `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetAll?projectId=${projectId}&page=${page}&pageSize=${count}&annotation=${annotation}`;

  const response = await AxiosInstance.get(url);
  // Return the complete response data
  return response.data;
});

export const fetchImageDetailsById = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    imageUrlModel: Image[];
  },
  string
>("Images/GetById", async (projectImageId) => {
  let url = `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetById?projectImageId=${projectImageId}`;

  const response = await AxiosInstance.get(url);
  // Return the complete response data
  return response.data;
});

export const fetchListOfAiModels = createAsyncThunk<any, string>(
  "images/fetchListOfAiModels",
  async (projectId) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/AILabel/GetAllAILabelingModel`,
      {
        params: { projectId },
      }
    );
    return response.data;
  }
);

// Async thunk for fetching list of AI labels
export const fetchListOfAiLabels = createAsyncThunk<any, { model_id: string }>(
  "images/fetchListOfAiLabels",
  async ({ model_id }) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/AILabel/GetLabelByModelId?modelId=${model_id}`
    );
    return response.data;
  }
);

export const fetchLabelsConditionally = createAsyncThunk<
  any, // replace with actual type if known
  { model_id: string; ProjectId: string }
>(
  "images/fetchLabelsConditionally",
  async ({ model_id, ProjectId }, { dispatch }) => {
    try {
      console.log("🔄 Fetching AI Details for ProjectId:", ProjectId);

      const aiDetailsResponse = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/LabelMapping/GetAll?ProjectId=${ProjectId}`
      );

      const aiDetails = aiDetailsResponse.data;
      console.log("✅ AI Details Response:", aiDetails);

      if (Array.isArray(aiDetails) && aiDetails.length > 0) {
        console.log("📌 AI Details found, returning directly.");
        return aiDetails;
      } else {
        console.log("⚠️ AI Details empty, fetching from model_id:", model_id);
        const aiLabelsResponse = await dispatch(
          fetchListOfAiLabels({ model_id })
        );
        console.log(
          "✅ AI Labels fetched from model:",
          aiLabelsResponse.payload
        );
        return aiLabelsResponse.payload;
      }
    } catch (error) {
      console.error("❌ Error in fetchLabelsConditionally:", error);
      throw error;
    }
  }
);

export const fetchClassificationImagesfor = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    isUploader: boolean;
    imageUrlModel: Image[];
  },
  FetchImagesParams
>(
  "images/fetchClassificationImagesfor",
  async ({ projectId, page, count, annotation }) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetAll?projectId=${projectId}&page=${page}&pageSize=${count}&annotation=${annotation}`
    );
    // Return the complete response data
    return response.data;
  }
);

let imagesUploadController: AbortController | null = null;

export const fetchImagesNewClass = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    isUploader: boolean;
    imageUrlModel: Image[];
  },
  FetchImagesParams
>(
  "images/fetchImagesNewClass",
  async ({ projectId, page, count, annotation }) => {
    let url = `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetAll?projectId=${projectId}&page=${page}&pageSize=${count}&annotation=${annotation}`;

    const response = await AxiosInstance.get(url);
    // Return the complete response data
    return response.data;
  }
);

// Async thunk for fetching image by projectImageId
export const fetchImageById = createAsyncThunk<
  {
    totalCount: number;
    annotatedImageCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    imageUrlModel: Image[];
  },
  { projectImageId: string }
>("images/fetchImageById", async ({ projectImageId }) => {
  const response = await AxiosInstance.get(
    `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetById?projectImageId=${projectImageId}`
  );
  return response.data;
});

export const fetchAnnotatedImages = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    imageUrlModel: Image[];
  },
  FetchImagesParams
>("images/fetchAnnotatedImages", async ({ projectId, page, count }) => {
  if (imagesUploadController) {
    imagesUploadController.abort();
    imagesUploadController = null; // Reset after aborting
  }
  const response = await AxiosInstance.get(
    `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetAllImagesWithAnnotation?projectId=${projectId}&page=${page}&pageSize=${count}`
  );
  // Return the complete response data
  return response.data;
});

export const fetchFavourites = createAsyncThunk<any>(
  "/api/Project/GetAllFavorites",
  async () => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/GetAllFavorites`
    );
    // Return the complete response data
    return response.data;
  }
);

// Async thunk for uploading images
export const uploadImages = createAsyncThunk<Image[], FormData>(
  "api/Images/Upload",
  async (formData: FormData) => {
    const response = await AxiosInstance.post(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/Upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }
);

// Async thunk for uploading dataset images
export const uploadDatasetImages = createAsyncThunk<
  any,
  { projectId: string; image: File[]; label: File[]; data: File | null }
>(
  "api/Dataset/upload-images",
  async ({ projectId, image, label, data }, { rejectWithValue }) => {
    try {
      // Use FormData for file uploads
      const formData = new FormData();

      // Append projectId
      formData.append("ProjectId", projectId);

      // Append image files
      image.forEach((imgFile) => {
        formData.append("Image", imgFile);
      });

      // Append label files
      label.forEach((lblFile) => {
        formData.append("Label", lblFile);
      });

      // Append data file (yaml)
      if (data) {
        formData.append("Data", data);
      }

      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Dataset/UploadDatasetYOLO`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error in uploadDatasetImages thunk:", error);
      return rejectWithValue(
        error.response?.data || "Error uploading dataset images"
      );
    }
  }
);

// API for COCO format upload
export const uploadDatasetImagesCOCO = createAsyncThunk<
  any,
  { projectId: string; image: File[]; annotationData: File | null }
>(
  "api/Dataset/upload-images-coco",
  async ({ projectId, image, annotationData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("ProjectId", projectId);

      image.forEach((imgFile) => {
        formData.append("Image", imgFile);
      });

      if (annotationData) {
        formData.append("AnnotationData", annotationData);
      }

      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Dataset/UploadDatasetCOCO`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error in uploadDatasetImagesCOCO thunk:", error);
      return rejectWithValue(
        error.response?.data || "Error uploading COCO dataset images"
      );
    }
  }
);

export const uploadDatasetImagesPASCALVOC = createAsyncThunk<
  any,
  { projectId: string; image: File[]; annotations: File[] }
>(
  "api/Dataset/upload-images-pascal-voc",
  async ({ projectId, image, annotations }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("ProjectId", projectId);

      image.forEach((imgFile) => {
        formData.append("Image", imgFile);
      });

      annotations.forEach((Annotat) => {
        formData.append("Annotations", Annotat);
      });

      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Dataset/UploadDatasetPASCALVOC`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error in uploadDatasetImagesPASCALVOC thunk:", error);
      return rejectWithValue(
        error.response?.data || "Error uploading PASCAL-VOC dataset images"
      );
    }
  }
);

export const uploadImagesDelete = createAsyncThunk<string, string>(
  "/api/Images/Delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/Delete?imageId=${id}`
      );
      return response.data;
    } catch (error: any) {
      // Explicitly cast error to 'any' or handle type check
      // Log the full error response
      console.error("Error in uploadImagesDelete thunk:", error);

      // Optionally, return a custom error message or response
      return rejectWithValue(error.response?.data || "Error deleting image");
    }
  }
);

export const fetchImageLabelDetails = createAsyncThunk(
  "/api/Images/ImageData",
  async (imageId: string) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/ImageData?imageId=${imageId}`
    );
    return response.data;
  }
);

export const fetchImageModelDetails = createAsyncThunk(
  "/api/Images/ImageData",
  async (projectId: string) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/LabelMapping/GetModelId?projectId=${projectId}`
    );
    return response.data;
  }
);

export const addLabelMapping = createAsyncThunk(
  "/api/LabelMapping/Add",
  async (mappingData: any, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/LabelMapping/Add`,
        mappingData
      );
      return response.data;
    } catch (error: any) {
      console.error("Error in addLabelMapping thunk:", error);
      return rejectWithValue(
        error.response?.data || "Error adding label mapping"
      );
    }
  }
);

export const fetchImagesByProjectLabel = createAsyncThunk<
  {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    annotatedImageCount: number;
    imageUrlModel: Image[];
  },
  FetchImagesLabelParams
>(
  "images/fetchImagesByProjectLabel",
  async ({ labelId, projectId, page, count }) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/GetImageByLabel?projectlabelId=${labelId}&projectId=${projectId}&page=${page}&pageSize=${count}`
    );
    // Return the complete response data
    return response.data;
  }
);

// Create the slice
const ProjectImagesUploaded = createSlice({
  name: "images",
  initialState,
  reducers: {
    updateImageInList: (state, action: PayloadAction<UpdateImagePayload>) => {
      const { id, updatedImage } = action.payload;

      const index = state.annotationImages.findIndex(
        (image) => image.id?.toLowerCase() === id?.toLowerCase()
      );

      if (index !== -1) {
        state.annotationImages[index] = updatedImage;
      }
    },
    clearAnnotationImages: (state) => {
      state.annotationImages = [];
    },
    updateLabelColor: (
      state,
      action: PayloadAction<UpdateImageLabelColorPayload>
    ) => {
      const { labelId, color } = action.payload;

      state.annotationImages = state.annotationImages.map((image) => ({
        ...image,
        annotations: image.annotations.map((annotation: any) =>
          annotation.lableId === labelId
            ? { ...annotation, labelColor: color }
            : annotation
        ),
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchListOfAiModels.fulfilled, (state, action) => {
        state.listofAiModels = action.payload;
      })
      .addCase(fetchListOfAiModels.rejected, (state, action) => {
        state.listofAiModels = [];
      })
      .addCase(fetchListOfAiLabels.fulfilled, (state, action) => {
        state.listofAiLabels = action.payload;
      })
      .addCase(fetchListOfAiLabels.rejected, (state, action) => {
        state.listofAiLabels = [];
      })
      .addCase(fetchLabelsConditionally.fulfilled, (state, action) => {
        state.listofAiLabels = action.payload;
      })
      .addCase(fetchLabelsConditionally.rejected, (state, action) => {
        state.listofAiLabels = [];
      });
    builder
      .addCase(fetchImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImages.fulfilled, (state, action) => {
        state.loading = false;
        state.projectImagesUploaded = action.payload.imageUrlModel;
        //state.annotationImages = action.payload.imageUrlModel; // Assign the images
        if (action.payload.currentPage > 2) {
          state.annotationImages.push(...action.payload.imageUrlModel);
        } else {
          //state.projectId = action.payload.projectId;
          state.annotationImages = action.payload.imageUrlModel;
        }

        state.totalCount = action.payload.totalCount; // Store additional response data
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.annotatedImageCount = action.payload.annotatedImageCount;
        state.isAdmin = action.payload.isAdmin;
      })
      .addCase(fetchImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch images.";
      })

      .addCase(fetchImagesNew.pending, (state) => {
        state.images = [];
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImagesNew.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload.imageUrlModel;

        state.totalCountUploaded = action.payload.totalCount;
        state.totalPagesUploaded = action.payload.totalPages;
        state.currentPageUploaded = action.payload.currentPage;
        state.pageSizeUploaded = action.payload.pageSize;
        state.annotatedImageCountUploaded = action.payload.annotatedImageCount;
      })
      .addCase(fetchImagesNew.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch images.";
      })
      .addCase(fetchImagesNewClass.pending, (state) => {
        state.images = [];
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImagesNewClass.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload.imageUrlModel;
        if (action.payload.currentPage > 2) {
          state.allAnnotationImages.push(...action.payload.imageUrlModel);
        } else {
          //state.projectId = action.payload.projectId;
          state.allAnnotationImages = action.payload.imageUrlModel;
        }
        state.totalCount = action.payload.totalCount; // Store additional response data
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.annotatedImageCount = action.payload.annotatedImageCount;
      })
      .addCase(fetchImagesNewClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch images.";
      })

      .addCase(fetchImageDetailsById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchImageDetailsById.fulfilled, (state, action) => {
        state.loading = false;
        state.annotationImageDetails = action.payload.imageUrlModel;
      })
      .addCase(fetchImageDetailsById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch image details";
      })

      .addCase(fetchAnnotatedImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnotatedImages.fulfilled, (state, action) => {
        state.loading = false;
        //state.annotationImages = action.payload.imageUrlModel; // Assign the images

        if (action.payload.currentPage > 2) {
          state.annotationImages.push(...action.payload.imageUrlModel);
        } else {
          state.annotationImages = action.payload.imageUrlModel;
        }
        state.projectImagesUploaded = action.payload.imageUrlModel;
        state.totalCountUploaded = action.payload.totalCount;
        state.totalPagesUploaded = action.payload.totalPages;
        state.currentPageUploaded = action.payload.currentPage;
        state.pageSizeUploaded = action.payload.pageSize;
        // state.annotatedImageCount = action.payload.annotatedImageCount;
        //state.totalCount = action.payload.totalCount; // Store additional response data
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchAnnotatedImages.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch Annotated images.";
      })

      .addCase(uploadImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(uploadImages.fulfilled, (state, action) => {
        state.loading = false;
        if (state.projectImagesUploaded) {
          state.projectImagesUploaded.push(...action.payload);
        } else {
          state.projectImagesUploaded = action.payload;
        }
      })

      .addCase(uploadImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to upload images.";
      })
      .addCase(fetchFavourites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFavourites.fulfilled, (state, action) => {
        state.loading = false;
        state.favouritesList = action.payload;
      })
      .addCase(fetchFavourites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "";
      })
      .addCase(fetchImageLabelDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImageLabelDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.imageLabelDetails = action.payload;
      })
      .addCase(fetchImageLabelDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "";
      })

      .addCase(fetchImagesByProjectLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImagesByProjectLabel.fulfilled, (state, action) => {
        state.loading = false;
        state.projectImagesUploaded = action.payload.imageUrlModel;
        state.totalCountUploaded = action.payload.totalCount;
        state.totalPagesUploaded = action.payload.totalPages;
        state.currentPageUploaded = action.payload.currentPage;
        state.pageSizeUploaded = action.payload.pageSize;
        state.annotatedImageCount = action.payload.annotatedImageCount;
      })
      .addCase(fetchImagesByProjectLabel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch images.";
      })

      // Extra reducers for fetchImageById
      .addCase(fetchImageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImageById.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchImageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch image by id.";
      });
  },
});

export const { updateImageInList, clearAnnotationImages, updateLabelColor } =
  ProjectImagesUploaded.actions;

// Export the reducer
export default ProjectImagesUploaded.reducer;
